import { useState } from "react";
import { ChevronRight, FileText, Loader2, Plus, Trash2, X } from "lucide-react";
import { PAGES } from "@/cms/blockRegistry";
import { slugify } from "@/cms/slug";

/** Modal for naming a new custom page. Slug is derived from the title and
 * shown live so the admin knows what URL they're committing to; kept
 * editable in case the auto-slug collides or they'd rather have something
 * shorter. */
function NewPageModal({ existingSlugs, onCreate, onClose }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const effectiveSlug = slugTouched ? slug : slugify(title);
  const collision = effectiveSlug && existingSlugs.has(effectiveSlug);

  const handleCreate = async () => {
    if (!title.trim()) {
      setError("Give the page a title.");
      return;
    }
    if (!effectiveSlug) {
      setError("That title doesn't produce a usable URL — try adding a letter or number.");
      return;
    }
    if (collision) {
      setError("That URL is already taken by another page.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      await onCreate(effectiveSlug, title.trim());
    } catch (err) {
      setError(err?.message || "Something went wrong.");
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#0B2421]">New Page</h2>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="text-sm font-semibold text-[#0B2421] mb-1.5 block">
          Page title
        </label>
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. FAQ"
          className="w-full h-11 rounded-xl border border-gray-200 bg-white px-3.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-[#0B2421]/30 mb-4"
        />

        <label className="text-sm font-semibold text-[#0B2421] mb-1.5 block">
          URL
        </label>
        <div className="flex items-center h-11 rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm mb-1">
          <span className="text-gray-400 shrink-0">/page/</span>
          <input
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
            className="flex-1 bg-transparent outline-none text-gray-700 min-w-0"
          />
        </div>
        {collision && (
          <p className="text-xs text-red-500 mb-3">
            A page already uses this URL.
          </p>
        )}

        {error && <p className="text-xs text-red-500 mt-2 mb-2">{error}</p>}

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="h-11 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-40 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="h-11 px-5 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold disabled:opacity-40 flex items-center gap-1.5 text-sm"
          >
            {creating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create Page
          </button>
        </div>
      </div>
    </div>
  );
}

/** Landing view for "Edit Site" — pick which page to edit. The fixed
 * pages come from blockRegistry.js's PAGES; below them, every
 * admin-created custom page (any FAQ/policy/etc. page made via
 * "+ New Page") shows up too, fetched from the customPages collection.
 * @param {{
 *   onSelect: (pageKey: string) => void;
 *   customPages: { slug: string, title: string }[];
 *   loadingCustomPages: boolean;
 *   onCreatePage: (slug: string, title: string) => Promise<void>;
 *   onDeletePage: (slug: string) => Promise<void>;
 * }} props
 */
export default function PagePicker({
  onSelect,
  customPages = [],
  loadingCustomPages = false,
  onCreatePage,
  onDeletePage,
}) {
  const [showNewPage, setShowNewPage] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState(null);

  const existingSlugs = new Set([
    ...Object.keys(PAGES),
    ...customPages.map((p) => p.slug),
  ]);

  const handleCreate = async (slug, title) => {
    await onCreatePage(slug, title);
    setShowNewPage(false);
    onSelect(slug);
  };

  const handleDelete = async (e, slug) => {
    e.stopPropagation();
    if (!window.confirm("Delete this page? It will disappear from the site and its URL will 404.")) {
      return;
    }
    setDeletingSlug(slug);
    try {
      await onDeletePage(slug);
    } finally {
      setDeletingSlug(null);
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(PAGES).map(([key, { label }]) => (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white shadow-sm p-5 text-left hover:border-[#0B2421]/30 hover:shadow-md transition-all"
          >
            <div className="h-11 w-11 rounded-xl bg-[#0B2421]/5 flex items-center justify-center shrink-0">
              <FileText className="h-5 w-5 text-[#0B2421]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#0B2421]">{label}</p>
              <p className="text-xs text-gray-400">Edit page content</p>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300" />
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mt-8 mb-4">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">
          Custom Pages
        </p>
        <button
          type="button"
          onClick={() => setShowNewPage(true)}
          className="h-9 px-3.5 rounded-lg border border-dashed border-gray-300 text-xs font-semibold text-gray-500 hover:bg-gray-50 flex items-center gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          New Page
        </button>
      </div>

      {loadingCustomPages ? (
        <div className="flex items-center justify-center py-10 text-gray-400 gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      ) : customPages.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center text-gray-400 text-sm">
          No custom pages yet — create one for an FAQ, a policy page, or
          anything else that doesn't need its own dedicated design.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {customPages.map(({ slug, title }) => (
            <div
              key={slug}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(slug)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onSelect(slug);
              }}
              className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white shadow-sm p-5 text-left hover:border-[#0B2421]/30 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="h-11 w-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#0B2421] truncate">
                  {title || slug}
                </p>
                <p className="text-xs text-gray-400 truncate">/page/{slug}</p>
              </div>
              <button
                type="button"
                onClick={(e) => handleDelete(e, slug)}
                disabled={deletingSlug === slug}
                className="h-8 w-8 rounded-lg border border-red-200 flex items-center justify-center text-red-500 hover:bg-red-50 disabled:opacity-40 shrink-0"
              >
                {deletingSlug === slug ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {showNewPage && (
        <NewPageModal
          existingSlugs={existingSlugs}
          onCreate={handleCreate}
          onClose={() => setShowNewPage(false)}
        />
      )}
    </div>
  );
}
